﻿package org.zq.pixelset.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import org.zq.pixelset.model.NormalizedRect
import kotlin.math.min

@Composable
fun CropOverlay(
    imageWidth: Int,
    imageHeight: Int,
    cropRect: NormalizedRect,
    onCropChange: (NormalizedRect) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (imageWidth <= 0 || imageHeight <= 0) return

    var activeHandle by remember { mutableStateOf<CropHandle?>(null) }
    var dragStartCrop by remember { mutableStateOf(cropRect) }
    var dragStartOffset by remember { mutableStateOf(Offset.Zero) }

    Box(modifier = modifier.fillMaxSize()) {
        Canvas(
            modifier = Modifier
                .fillMaxSize()
                .pointerInput(imageWidth, imageHeight, cropRect) {
                    detectDragGestures(
                        onDragStart = { offset ->
                            activeHandle = hitTest(
                                offset,
                                imageWidth,
                                imageHeight,
                                cropRect,
                                size.width.toFloat(),
                                size.height.toFloat(),
                            )
                            dragStartCrop = cropRect
                            dragStartOffset = offset
                        },
                        onDrag = { change, _ ->
                            if (activeHandle == null) return@detectDragGestures
                            val fit = computeFitRect(
                                size.width.toFloat(),
                                size.height.toFloat(),
                                imageWidth.toFloat(),
                                imageHeight.toFloat(),
                            )
                            val dx = (change.position.x - dragStartOffset.x) / fit.width
                            val dy = (change.position.y - dragStartOffset.y) / fit.height
                            onCropChange(adjustCrop(dragStartCrop, dx, dy))
                        },
                        onDragEnd = { activeHandle = null },
                        onDragCancel = { activeHandle = null },
                    )
                },
        ) {
            val fit = computeFitRect(size.width, size.height, imageWidth.toFloat(), imageHeight.toFloat())
            val cropScreen = Rect(
                left = fit.left + cropRect.left * fit.width,
                top = fit.top + cropRect.top * fit.height,
                right = fit.left + cropRect.right * fit.width,
                bottom = fit.top + cropRect.bottom * fit.height,
            )
            val dim = Color.Black.copy(alpha = 0.45f)
            drawRect(dim, topLeft = Offset.Zero, size = Size(size.width, cropScreen.top))
            drawRect(dim, topLeft = Offset(0f, cropScreen.bottom), size = Size(size.width, size.height - cropScreen.bottom))
            drawRect(
                dim,
                topLeft = Offset(0f, cropScreen.top),
                size = Size(cropScreen.left, cropScreen.height),
            )
            drawRect(
                dim,
                topLeft = Offset(cropScreen.right, cropScreen.top),
                size = Size(size.width - cropScreen.right, cropScreen.height),
            )
            drawRect(
                color = Color.White,
                topLeft = Offset(cropScreen.left, cropScreen.top),
                size = cropScreen.size,
                style = Stroke(width = 2.dp.toPx()),
            )
        }
    }
}

private enum class CropHandle { Move }

private fun hitTest(
    offset: Offset,
    imageW: Int,
    imageH: Int,
    crop: NormalizedRect,
    containerW: Float,
    containerH: Float,
): CropHandle? {
    val fit = computeFitRect(containerW, containerH, imageW.toFloat(), imageH.toFloat())
    val screen = Rect(
        fit.left + crop.left * fit.width,
        fit.top + crop.top * fit.height,
        fit.left + crop.right * fit.width,
        fit.top + crop.bottom * fit.height,
    )
    return if (screen.contains(offset)) CropHandle.Move else null
}

private fun adjustCrop(start: NormalizedRect, dx: Float, dy: Float): NormalizedRect {
    val w = start.right - start.left
    val h = start.bottom - start.top
    val l = (start.left + dx).coerceIn(0f, 1f - w)
    val t = (start.top + dy).coerceIn(0f, 1f - h)
    return NormalizedRect(l, t, l + w, t + h).clamped()
}

private fun computeFitRect(containerW: Float, containerH: Float, imageW: Float, imageH: Float): Rect {
    val scale = min(containerW / imageW, containerH / imageH)
    val dw = imageW * scale
    val dh = imageH * scale
    val ox = (containerW - dw) / 2f
    val oy = (containerH - dh) / 2f
    return Rect(ox, oy, ox + dw, oy + dh)
}
